import React, { useState, useEffect } from "react";
import { Tab, Message, Dimmer, Loader } from "semantic-ui-react";
import QnaireAnalysisPage from "./QnaireAnalysisPage";
import CustomAnalysisQueryPage from "./CustomAnalysisQueryPage";
import CrossTableAnalysisPage from "./CrossTableAnalysisPage";
import { connect } from "react-redux";
import _ from "lodash";
import { toast } from "react-toastify";
import { useLocation, useHistory } from "react-router-dom";
import * as QUESTIONAIRE_ACTIONS from "../../../actions/questionaireActions";
import * as DEEPANALYSIS_ACTIONS from "../../../actions/deepanalysisActions";

const DeepAnalysis = (props) => {
    const location = useLocation();
    const history = useHistory();
    const [state, setState] = useState({
        loading: true,
        denied: false,
    });
    const [activeIndex, setActiveIndex] = useState(0);

    // Get tab from URL search params
    const getTabFromUrl = () => {
        const searchParams = new URLSearchParams(location.search);
        const tabParam = searchParams.get('tab');

        if (tabParam === 'custom') {
            return canReadCustomAnalysis() ? (canReadQuestionnaireAnalysis() ? 1 : 0) : 0;
        } else if (tabParam === 'crosstable') {
            // Cross table is always the last tab if custom analysis is available
            if (canReadCustomAnalysis()) {
                return canReadQuestionnaireAnalysis() ? 2 : 1;
            }
            return 0;
        } else if (tabParam === 'questionnaire') {
            return 0;
        }
        return 0;
    };

    // Update URL when tab changes
    const updateUrlForTab = (tabIndex) => {
        const searchParams = new URLSearchParams(location.search);

        // Determine which tab this index represents
        let tabName = 'questionnaire';
        if (canReadQuestionnaireAnalysis() && canReadCustomAnalysis()) {
            // Both tabs available: 0=questionnaire, 1=custom, 2=crosstable
            if (tabIndex === 1) {
                tabName = 'custom';
            } else if (tabIndex === 2) {
                tabName = 'crosstable';
            } else {
                tabName = 'questionnaire';
            }
        } else if (canReadCustomAnalysis() && !canReadQuestionnaireAnalysis()) {
            // Only custom analysis available: 0=custom, 1=crosstable
            if (tabIndex === 1) {
                tabName = 'crosstable';
            } else {
                tabName = 'custom';
            }
        }

        searchParams.set('tab', tabName);
        history.replace({
            pathname: location.pathname,
            search: searchParams.toString()
        });
    };

    const hasPermissions = (category) => {
        return (
            !_.isEmpty(props.userPermissions) &&
            props.userPermissions.hasOwnProperty(category)
        );
    };

    const isAdmin = () => {
        return (
            hasPermissions("Other") && props.userPermissions["Other"].actions.admin
        );
    };

    const canReadQuestionnaireAnalysis = () => {
        return hasPermissions('Deep Analysis') && props.userPermissions['Deep Analysis'].actions["VIEW QUESTIONNAIRE ANALYSIS"]
            || isAdmin();
    }

    const canReadCustomAnalysis = () => {
        return hasPermissions('Deep Analysis') && props.userPermissions['Deep Analysis'].actions["VIEW CUSTOM ANALYSIS"]
            || isAdmin();
    }


    const handleTabChange = (e, { activeIndex }) => {
        // Clear the deepAnalysisQuery when switching tabs
        props.clearDeepAnalysisQueryAction();
        setActiveIndex(activeIndex);
        updateUrlForTab(activeIndex);
    };

    // Set initial tab from URL when component mounts or permissions change
    useEffect(() => {
        if (!state.loading && !state.denied) {
            const tabFromUrl = getTabFromUrl();
            setActiveIndex(tabFromUrl);
        }
    }, [state.loading, state.denied, location.search]);

    useEffect(() => {
        if (!_.isEmpty(props.tenant)) {
            if (props.isPermissionsFetched) {
                if ((!canReadQuestionnaireAnalysis() && !canReadCustomAnalysis())) {
                    setState({
                        ...state,
                        loading: false,
                        denied: true,
                    });
                    toast.error("You do not have permission to view this page.");
                } else {
                    setState({
                        ...state,
                        loading: false,
                        denied: false,
                    });
                }
            }
        }
    }, [props.tenant, props.isPermissionsFetched]);

    // Tab panes
    const panes = [
        canReadQuestionnaireAnalysis() && {
            menuItem: 'Questionnaire Analysis',
            render: () => (
                <Tab.Pane attached={false}>
                    <QnaireAnalysisPage
                        tenant={props.tenant}
                        questionnaires={props.questionnaires}
                    />
                </Tab.Pane>
            ),
        },
        canReadCustomAnalysis() && {
            menuItem: 'Custom Analysis',
            render: () => (
                <Tab.Pane attached={false}>
                    <CustomAnalysisQueryPage
                        tenant={props.tenant}
                        questionnaires={props.questionnaires}
                    />
                </Tab.Pane>
            ),
        },
        canReadCustomAnalysis() && {
            menuItem: 'Cross Table Analysis',
            render: () => (
                <Tab.Pane attached={false}>
                    <CrossTableAnalysisPage
                        tenant={props.tenant}
                        questionnaires={props.questionnaires}
                    />
                </Tab.Pane>
            ),
        },
    ].filter(Boolean);

    if (state.loading) {
        return (
            <Dimmer active inverted>
                <Loader>Loading...</Loader>
            </Dimmer>
        );
    }
    if (state.denied) {
        return <Message negative content="You do not have permission to view this page." />;
    }
    return (
        <Tab
            menu={{ secondary: false, pointing: true }}
            panes={panes}
            activeIndex={activeIndex}
            onTabChange={handleTabChange}
        />
    );
}
const mapStateToProps = (state) => {
    return {
        tenant: state.tenantManagement.activeTenant,
        userPermissions: _.isEmpty(state.authUserRoles.permissions)
            ? {}
            : state.authUserRoles.permissions,
        isPermissionsFetched: state.isPermissionsFetched === true,
        questionnaires: _.isEmpty(state.questionaires) ? [] : Object.values(state.questionaires),
    };
};

export default connect(
    mapStateToProps,
    // map dispatch to props
    (dispatch) => ({
        clearDeepAnalysisQueryAction: () => dispatch(DEEPANALYSIS_ACTIONS.clearDeepAnalysisQueryAction()),
    })
)(DeepAnalysis);